import { Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import * as yup from "yup";
import { Button } from "../../../components/ui/button";
import Card from "../../../components/ui/card-snippet";
import { useLocation, useNavigate } from "react-router-dom";
import { respEnum, IntMax, IntMin } from "../../../constants/appConstant";
import {
  FormikInput,
  FormikSwitch,
  FormikSelect,
  PhoneInputField,
} from "../../../components/formik-components";
import Common from "../../../helper/common";
import { useQueryParams } from "../../../contexts/queryParamsContext";

const formValidations = yup.object({
  name: yup.string().required("Name is required"),
  phone: yup
    .string()
    .required("Phone Number is required")
    .min(5)
    .matches(/^\+?[1-9]\d{1,14}$/, "Phone number must be valid"),
  streetno: yup
    .string()
    .nullable()
    .max(50, "Street number cannot exceed 50 characters"),
  houseno: yup
    .string()
    .nullable()
    .max(50, "House number cannot exceed 50 characters"),
  address: yup
    .string()
    .nullable()
    .max(255, "Address cannot exceed 255 characters"),
});

export default function AddClubUser() {
  const navigate = useNavigate();
  const formikRef = useRef();
  const location = useLocation();
  const { id, setId } = useQueryParams();

  useEffect(() => {
    if (id) {
      getClubUserDetail();
    }

    return () => {
      setId(null);
    };
  }, [id]);

  const formInitialValue = {
    name: "",
    phone: "",
    phoneCode: "+92",
    phoneNum: "",
    streetno: "",
    houseno: "",
    address: "",
    isactive: true,
  };

  const getClubUserDetail = async () => {
    let response = await Common.ApiService.getInstance().request(
      `GetPrivateClubUserDetails?id=${id}`
    );

    if (response.status == respEnum.Success && response?.data) {
      let d = response?.data;

      // Transform the data to match the PhoneInputField expectations
      const transformedData = {
        ...d,
        phone: d.phoneCode && d.phone ? `${d.phoneCode}${d.phone}` : "",
        phoneNum: d.phone || "",
        phoneCode: d.phoneCode || "+92",
        streetno: d.streetno || "",
        houseno: d.houseno || "",
        address: d.address || "",
      };

      if (formikRef.current) {
        formikRef.current.setValues(transformedData);
      }
    }
  };

  const onSubmitData = async (d, action) => {
    let data = {
      name: d.name,
      phone: d.phoneNum, // Use phoneNum (without country code)
      phoneCode: d.phoneCode, // Use phoneCode separately
      streetno: d.streetno,
      houseno: d.houseno,
      address: d.address,
      isactive: d.isactive,
      id: id ? +id : 0,
    };

    let response = await Common.ApiService.getInstance().request(
      "AddUpdatePrivateUser",
      data,
      "POST"
    );
    action.setSubmitting(false);

    if (response.status == respEnum.Success) {
      Common.SweetAlert.alert(response.message);
      setTimeout(() => {
        navigate("/app/clubusers");
      }, 1000);
    }
  };

  return (
    <Formik
      initialValues={formInitialValue}
      validationSchema={formValidations}
      innerRef={formikRef}
      validateOnBlur={false}
      validateOnChange={false}
      onSubmit={onSubmitData}
    >
      {(formikProps) => {
        formikRef.current = formikProps;
        return (
          <>
            <div className="space-y-6 mb-5">
              <Card title={id ? "Edit Private User" : "Create Private User"}>
                <div className="md:grid md:grid-cols-12 gap-4 p-5">
                  <div className="flex flex-col col-span-3 gap-2">
                    <FormikInput
                      formikProps={formikProps}
                      name={"name"}
                      placeholder={"Enter Full Name"}
                      label={"Name"}
                      maxLength="100"
                    />
                  </div>

                  <div className="flex flex-col col-span-3 gap-2">
                    <PhoneInputField formikProps={formikProps} />
                  </div>

                  <div className="flex flex-col col-span-2 gap-2">
                    <FormikInput
                      formikProps={formikProps}
                      name={"streetno"}
                      placeholder={"Enter Street Number"}
                      label={"Street No"}
                      maxLength="50"
                    />
                  </div>

                  <div className="flex flex-col col-span-2 gap-2">
                    <FormikInput
                      formikProps={formikProps}
                      name={"houseno"}
                      placeholder={"Enter House Number"}
                      label={"House No"}
                      maxLength="50"
                    />
                  </div>

                  <div className="flex flex-col col-span-2 gap-2">
                    <FormikSwitch
                      formikProps={formikProps}
                      name={"isactive"}
                      label={"Is Active"}
                    />
                  </div>

                  <div className="flex flex-col col-span-12 gap-2">
                    <FormikInput
                      formikProps={formikProps}
                      name={"address"}
                      placeholder={"Enter Address"}
                      label={"Address"}
                      maxLength="500"
                      isTextarea={true}
                      rows={3}
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 p-5 pt-0">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate("/app/clubusers")}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={formikProps.isSubmitting}
                    onClick={formikProps.handleSubmit}
                  >
                    {formikProps.isSubmitting ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Saving...
                      </div>
                    ) : id ? (
                      "Update"
                    ) : (
                      "Save"
                    )}
                  </Button>
                </div>
              </Card>
            </div>
          </>
        );
      }}
    </Formik>
  );
}
